/**
 * NexoPagosView -- Control de cobros de Nexo Digital (#/nexo-pagos).
 * Las ventas de Nexo son sus suscripciones: esta vista lista los pagos de
 * nexo_private.nexo_pagos_suscripcion, permite el alta manual y muestra los
 * totales del mes calculados en vivo. Formato financiero espejo de EC para
 * que la conciliacion de la cuenta compartida (BANCO-MULTINEGOCIO) los encuentre.
 *
 * Exclusiva Nexo: audience 'nexo' en NavBar (ningun rol dueno la ve) y
 * RequireAdmin en la API.
 *
 * PAGOS-NEXO-POR-CLIENTE-T1 (6-oct): el negocio se elige de la lista de activos (business_id
 * siempre), el monto se escribe como en Colombia y arranca en la tarifa, y "Registrar y activar"
 * deja la suscripcion activa en la misma accion. Las reglas viven en application/negocios/pagoNexo.js.
 *
 * Mission: NEXO-VENTAS-SUSCRIPCIONES-V1-001 (NX-T2) v1.1
 * @module presentation/views/NexoPagosView
 */

import { NexoPagosApiClient } from '../../infrastructure/api/NexoPagosApiClient.js';
import { BusinessApiClient } from '../../infrastructure/api/BusinessApiClient.js';
import { SubscriptionApiClient } from '../../infrastructure/api/SubscriptionApiClient.js';
import {
  negociosParaPago, leerMonto, montoParaVer, tarifaDeSuscripciones, mesQueQueda,
  cuerpoDelPago, motivoSinActivar, activaHasta
} from '../../application/negocios/pagoNexo.js';
import {
  leerCartera, motivoCartera, pagoDelMes, cuerpoAsignar, candidatoEnLista, motivoAsignar, mesParaVer
} from '../../application/negocios/carteraNexo.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const fmtCOP = (n) => '$' + new Intl.NumberFormat('es-CO').format(Number(n || 0));

function mesActual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtFecha(iso) {
  if (!iso) return '--';
  return formatearFecha(new Date(iso + 'T00:00:00'), { day: '2-digit', month: 'short', year: 'numeric' });
}

let mesFiltro = mesActual();

function boolBadge(v, labelSi, labelNo) {
  return v
    ? `<span style="background:#3fb95022;color:#3fb950;padding:2px 8px;border-radius:4px;font-size:0.875rem;font-weight:600">${labelSi}</span>`
    : `<span style="background:#8b949e22;color:#8b949e;padding:2px 8px;border-radius:4px;font-size:0.875rem;font-weight:600">${labelNo}</span>`;
}

function canalBadge(c) {
  const cfg = c === 'link-v10'
    ? { color: '#a371f7', label: 'link-v10' }
    : { color: '#58a6ff', label: c || 'panel' };
  return `<span style="background:${cfg.color}22;color:${cfg.color};padding:2px 8px;border-radius:4px;font-size:0.875rem;font-weight:600">${esc(cfg.label)}</span>`;
}

function showToast(msg, ok = true) {
  const t = document.createElement('div');
  t.style.cssText = `position:fixed;bottom:24px;right:24px;z-index:10000;background:${ok ? '#0d2818' : '#3d1418'};border:1px solid ${ok ? '#238636' : '#da3633'};color:${ok ? '#3fb950' : '#f85149'};padding:12px 18px;border-radius:8px;font-size:0.875rem;box-shadow:0 4px 16px rgba(0,0,0,0.4)`;
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 3500);
}

async function loadPagos() {
  const content = document.getElementById('np-content');
  if (!content) return;
  content.innerHTML = '<div style="padding:24px;color:#8b949e">Cargando pagos de Nexo...</div>';

  try {
    const res = await NexoPagosApiClient.list(mesFiltro);
    if (!res.ok) throw new Error(res.error || 'Error cargando pagos');
    const body = res.data || {};
    const pagos = body.data || [];
    const r = body.resumen || {};
    const h = body.historico || {};

    content.innerHTML = `
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:12px;margin-bottom:18px">
        <div class="np-card">
          <div class="np-card-label">Pagos del mes</div>
          <div class="np-card-value">${r.pagos ?? 0}</div>
        </div>
        <div class="np-card">
          <div class="np-card-label">Total del mes</div>
          <div class="np-card-value" style="color:#3fb950">${fmtCOP(r.total)}</div>
        </div>
        <div class="np-card">
          <div class="np-card-label">Historico</div>
          <div class="np-card-value" style="font-size:1.1rem">${h.pagos ?? 0} pagos &middot; ${fmtCOP(h.total)}</div>
        </div>
        <div class="np-card">
          <div class="np-card-label">Activados / En banco</div>
          <div class="np-card-value" style="font-size:1.1rem">${h.activados ?? 0} / ${h.en_banco ?? 0}</div>
        </div>
      </div>

      ${pagos.length ? `
      <div style="overflow-x:auto">
        <table class="np-table">
          <thead><tr>
            <th>Negocio</th>
            <th style="text-align:right">Monto</th>
            <th>Medio</th>
            <th>Fecha pago</th>
            <th>Mes</th>
            <th>Canal</th>
            <th>Activado</th>
            <th>Banco</th>
            <th>Notas</th>
          </tr></thead>
          <tbody>
            ${pagos.map(p => `
              <tr>
                <td style="font-weight:600">${esc(p.negocio_nombre)}</td>
                <td style="text-align:right;font-weight:600;color:#3fb950">${fmtCOP(p.monto)}</td>
                <td style="font-size:0.875rem">${esc(p.medio)}</td>
                <td style="font-size:0.875rem">${fmtFecha(p.fecha_pago)}</td>
                <td style="font-size:0.875rem;color:#8b949e">${esc(p.mes_cubierto)}</td>
                <td>${canalBadge(p.canal_activacion)}</td>
                <td>${boolBadge(p.activado, 'Activado', 'Pendiente')}</td>
                <td>${boolBadge(p.registrado_en_banco, 'En banco', 'Sin registrar')}</td>
                <td style="font-size:0.875rem;color:#8b949e;max-width:280px;white-space:pre-wrap">${esc(p.notas || '')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      ` : `<div style="padding:32px;text-align:center;color:#484f58">Sin pagos registrados para ${esc(mesFiltro)}. Usa "+ Registrar pago" o cambia el mes.</div>`}
    `;
  } catch (err) {
    content.innerHTML = `<div style="padding:24px;color:#f85149">Error: ${esc(err.message)}
      <br><br><button id="np-retry" class="np-btn">Reintentar</button></div>`;
    document.getElementById('np-retry')?.addEventListener('click', loadPagos);
  }
}

// LA CARTERA (PAGOS-NEXO-POR-CLIENTE-T1 punto 6): la calcula la API (nexo-api PR 455) y aqui solo se pinta.
// Por negocio, los meses que debe desde 2026-08 con "Registrar este mes"; aparte, los pagos sin negocio con "Asignar".
let cartera = null;

function filaNegocio(n) {
  const ultimo = n.ultimoPago
    ? `Ultimo pago ${fmtFecha(n.ultimoPago.fecha)} (${esc(mesParaVer(n.ultimoPago.mes))}, ${fmtCOP(n.ultimoPago.monto)})`
    : 'Sin pagos registrados en el panel';
  return `
    <div class="np-deudor" data-cartera-negocio="${esc(n.id)}">
      <div style="display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap;align-items:baseline">
        <div style="font-weight:600;color:#c9d1d9">${esc(n.nombre)}
          <span style="font-weight:400;font-size:0.875rem;color:#8b949e">${esc(n.plan)} &middot; tarifa ${fmtCOP(n.tarifa)}</span>
        </div>
        <div style="font-weight:600;color:#d29922">${fmtCOP(n.total)} &middot; ${n.meses.length} ${n.meses.length === 1 ? 'mes' : 'meses'}</div>
      </div>
      <div style="font-size:0.875rem;color:#8b949e;margin:2px 0 8px">${ultimo}</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        ${n.meses.map((m) => `
          <button type="button" class="np-btn np-mes-deuda" data-cartera-registrar data-negocio="${esc(n.id)}" data-mes="${esc(m.mes)}"
            title="Registrar el pago de ${esc(mesParaVer(m.mes))} de ${esc(n.nombre)}">
            ${esc(mesParaVer(m.mes))} &middot; ${fmtCOP(m.monto)} &middot; <strong>Registrar este mes</strong>
          </button>`).join('')}
      </div>
    </div>`;
}

function filaSinNegocio(p, negocios) {
  const candidato = candidatoEnLista(p, negocios);
  return `
    <div class="np-deudor" data-cartera-pago="${esc(p.id)}">
      <div style="display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap">
        <div><span style="color:#c9d1d9;font-weight:600">${esc(p.nombreEscrito || '(sin nombre escrito)')}</span>
          <span style="font-size:0.875rem;color:#8b949e">&middot; ${fmtFecha(p.fecha)} &middot; ${esc(mesParaVer(p.mes) || 'sin mes')}</span></div>
        <div style="font-weight:600;color:#3fb950">${fmtCOP(p.monto)}</div>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px;align-items:center">
        <select class="np-input" data-cartera-elegir style="flex:1 1 220px;min-width:0">
          <option value="">${negocios.length ? 'De que negocio es este pago?' : 'No se pudo leer la lista de negocios'}</option>
          ${negocios.map((n) => `<option value="${esc(n.id)}"${n.id === candidato ? ' selected' : ''}>${esc(n.nombre)}${n.id === candidato ? ' (candidato por el nombre)' : ''}</option>`).join('')}
        </select>
        <button type="button" class="np-btn" data-cartera-asignar style="background:#1f6feb;border-color:#388bfd;color:#fff">Asignar</button>
      </div>
      <div data-cartera-aviso style="font-size:0.875rem;min-height:14px;margin-top:4px;color:#f85149"></div>
    </div>`;
}

async function loadCartera() {
  const caja = document.getElementById('np-cartera');
  if (!caja) return;
  caja.innerHTML = '<div style="padding:12px 0;color:#8b949e">Leyendo la cartera...</div>';

  const res = await NexoPagosApiClient.cartera();
  if (!caja.isConnected) return;
  cartera = res.ok ? leerCartera(res) : null;
  if (!cartera) {
    const m = res.ok ? { sinPublicar: false, mensaje: 'La API no devolvio la cartera.' } : motivoCartera(res);
    caja.innerHTML = `<div data-cartera-error style="padding:12px;border:1px solid #30363d;border-radius:8px;color:${m.sinPublicar ? '#8b949e' : '#f85149'};font-size:0.875rem">
      ${esc(m.mensaje)} <button type="button" id="np-cartera-retry" class="np-btn" style="margin-left:8px">Reintentar</button></div>`;
    document.getElementById('np-cartera-retry')?.addEventListener('click', loadCartera);
    return;
  }

  // La lista de activos solo hace falta para asignar los pagos sin negocio.
  let negocios = [];
  if (cartera.sinNegocio.length) {
    const lr = await BusinessApiClient.list();
    if (!caja.isConnected) return;
    negocios = lr.ok ? negociosParaPago(lr) : [];
  }

  const c = cartera;
  const fuera = [
    c.fuera.sinCartera ? `${c.fuera.sinCartera} marcados sin cartera (prueba o cortesia)` : '',
    c.fuera.anuales ? `${c.fuera.anuales} con suscripcion anual` : ''
  ].filter(Boolean).join(' &middot; ');

  caja.innerHTML = `
    <h2 style="font-size:0.95rem;margin:0 0 4px;color:#c9d1d9">Cartera</h2>
    <p style="font-size:0.875rem;color:#8b949e;margin:0 0 12px">
      Meses que se deben desde ${esc(mesParaVer(c.desde))} (lo anterior se cobraba por el link viejo), al ${fmtFecha(c.fechaCorte)}.
      ${fuera ? `Fuera de la cartera: ${fuera}.` : ''}
    </p>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:12px;margin-bottom:14px">
      <div class="np-card"><div class="np-card-label">Deuda total</div><div class="np-card-value" data-cartera-total style="color:#d29922">${fmtCOP(c.totalDeuda)}</div></div>
      <div class="np-card"><div class="np-card-label">Negocios en deuda</div><div class="np-card-value" data-cartera-cuantos>${c.negociosEnDeuda}</div></div>
      <div class="np-card"><div class="np-card-label">Pagos sin negocio</div><div class="np-card-value">${c.sinNegocio.length}</div></div>
    </div>
    ${c.negocios.length
      ? `<div style="display:flex;flex-direction:column;gap:10px">${c.negocios.map(filaNegocio).join('')}</div>`
      : '<div style="padding:14px;color:#3fb950;font-size:0.875rem">Ningun negocio debe meses desde ' + esc(mesParaVer(c.desde)) + '.</div>'}
    ${c.sinNegocio.length ? `
      <h3 style="font-size:0.875rem;margin:20px 0 4px;color:#c9d1d9">Pagos sin negocio (${c.sinNegocio.length})</h3>
      <p style="font-size:0.875rem;color:#8b949e;margin:0 0 10px">No cubren ningun mes hasta que digas de quien son. Si el nombre escrito casa con un solo negocio, ya viene elegido.</p>
      <div style="display:flex;flex-direction:column;gap:10px">${c.sinNegocio.map((p) => filaSinNegocio(p, negocios)).join('')}</div>` : ''}
  `;

  caja.querySelectorAll('[data-cartera-registrar]').forEach((b) => b.addEventListener('click', () => {
    const n = c.negocios.find((x) => x.id === b.dataset.negocio);
    const pre = pagoDelMes(n, b.dataset.mes);
    if (pre) openModalAlta(pre);
  }));

  caja.querySelectorAll('[data-cartera-pago]').forEach((fila) => {
    const boton = fila.querySelector('[data-cartera-asignar]');
    const aviso = fila.querySelector('[data-cartera-aviso]');
    boton.addEventListener('click', async () => {
      aviso.textContent = '';
      const sel = fila.querySelector('[data-cartera-elegir]');
      const r = cuerpoAsignar(sel.value);
      if (!r.ok) { aviso.textContent = r.error; return; }
      boton.disabled = true;
      const res = await NexoPagosApiClient.asignarNegocio(fila.dataset.carteraPago, r.cuerpo);
      if (!res.ok) { aviso.textContent = motivoAsignar(res); boton.disabled = false; return; }
      const nombre = negocios.find((n) => n.id === r.cuerpo.businessId)?.nombre || r.cuerpo.businessId;
      const mes = res.data?.data?.mes_cubierto;
      showToast(`Pago asignado a ${nombre}${mes ? `: cubre ${mesParaVer(mes)}` : ''}`);
      loadCartera();
      loadPagos();
    });
  });
}

/**
 * El formulario de pago. `pre` llega desde "Registrar este mes" de la cartera: negocio, mes y monto ya puestos.
 * @param {{businessId: string, nombre: string, mes: string, monto: number}} [pre]
 */
function openModalAlta(pre = null) {
  document.getElementById('np-modal')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'np-modal';
  overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;padding:16px';
  overlay.innerHTML = `
    <div style="background:#161b22;border:1px solid #30363d;border-radius:10px;max-width:440px;width:100%;padding:20px;max-height:90vh;overflow-y:auto">
      <h3 style="margin:0 0 4px 0;font-size:1rem">Registrar pago Nexo</h3>
      <p style="margin:0 0 14px 0;font-size:0.875rem;color:#8b949e">"Registrar y activar" deja la suscripcion activa hasta el fin del mes cubierto. "Registrar pago" solo lo anota, sin activar.</p>
      <form id="np-form" novalidate style="display:flex;flex-direction:column;gap:10px">
        <label class="np-label">Negocio *
          <select id="np-f-negocio" class="np-input" disabled>
            <option value="">Cargando negocios activos...</option>
          </select>
        </label>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
          <label class="np-label">Monto (COP) *
            <input id="np-f-monto" type="text" inputmode="numeric" autocomplete="off" placeholder="75.000" class="np-input">
            <span id="np-f-tarifa" style="font-size:0.875rem;color:#8b949e;min-height:14px"></span>
          </label>
          <label class="np-label">Medio
            <select id="np-f-medio" class="np-input">
              <option value="transferencia">Transferencia</option>
              <option value="efectivo">Efectivo</option>
              <option value="nequi">Nequi</option>
              <option value="otro">Otro</option>
            </select>
          </label>
        </div>
        <label id="np-f-motivo-caja" class="np-label" style="display:none">Motivo del descuento
          <input id="np-f-motivo" type="text" placeholder="Ej: dias sin sistema por la actualizacion" class="np-input">
        </label>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
          <label class="np-label">Fecha del pago *
            <input id="np-f-fecha" type="date" value="${hoyISO()}" class="np-input">
          </label>
          <label class="np-label">Mes cubierto
            <input id="np-f-mes" type="month" value="${mesActual()}" class="np-input">
          </label>
        </div>
        <label class="np-label">Canal (solo para "Registrar pago")
          <select id="np-f-canal" class="np-input">
            <option value="panel">panel (registrado a mano aqui)</option>
            <option value="link-v10">link-v10 (pago llego por el link)</option>
          </select>
        </label>
        <label class="np-label">Notas
          <textarea id="np-f-notas" rows="2" placeholder="Nombre RTDB verificado, agrupacion bancaria, etc." class="np-input" style="resize:vertical"></textarea>
        </label>
        <div id="np-form-error" style="color:#f85149;font-size:0.875rem;min-height:16px"></div>
        <div style="display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap">
          <button type="button" id="np-cancel" class="np-btn">Cancelar</button>
          <button type="button" id="np-solo-registrar" class="np-btn">Registrar pago</button>
          <button type="submit" id="np-submit" class="np-btn" style="background:#238636;border-color:#2ea043;color:#fff">Registrar y activar</button>
        </div>
      </form>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  document.getElementById('np-cancel')?.addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  const $ = (id) => document.getElementById(id);
  const negocioSel = $('np-f-negocio');
  const montoInput = $('np-f-monto');
  const tarifaEl = $('np-f-tarifa');
  const fechaInput = $('np-f-fecha');
  const mesInput = $('np-f-mes');
  const errEl = $('np-form-error');

  let negocios = [];
  let tarifa = 0;
  let montoTocado = false;
  let mesTocado = false;

  // Desde la cartera: el mes y el monto ya vienen y la fecha no los pisa.
  if (pre) {
    mesInput.value = pre.mes;
    mesTocado = true;
    montoInput.value = montoParaVer(pre.monto);
    montoTocado = true;
  }

  const pintarDescuento = () => {
    const monto = leerMonto(montoInput.value);
    const descuento = tarifa > monto && monto > 0 ? tarifa - monto : 0;
    $('np-f-motivo-caja').style.display = descuento ? '' : 'none';
    tarifaEl.textContent = !tarifa ? ''
      : descuento ? `Tarifa ${fmtCOP(tarifa)} · descuento ${fmtCOP(descuento)}`
      : `Tarifa ${fmtCOP(tarifa)}`;
  };

  // La lista sale de los negocios ACTIVOS; sin ella no se registra (no hay texto libre de respaldo).
  BusinessApiClient.list().then((res) => {
    if (!overlay.isConnected) return;
    if (!res.ok) {
      negocioSel.innerHTML = '<option value="">No se pudo leer la lista de negocios</option>';
      errEl.textContent = `Sin la lista de negocios no se puede registrar: ${res.error || 'error de la API'}`;
      return;
    }
    negocios = negociosParaPago(res);
    negocioSel.innerHTML = `<option value="">Elige el negocio (${negocios.length} activos)</option>` +
      negocios.map((n) => `<option value="${esc(n.id)}">${esc(n.nombre)}</option>`).join('');
    negocioSel.disabled = false;
    negocioSel.focus();
    if (pre) {
      if (negocios.some((n) => n.id === pre.businessId)) {
        negocioSel.value = pre.businessId;
        negocioSel.dispatchEvent(new Event('change'));
      } else {
        errEl.textContent = `${pre.nombre} no aparece entre los negocios activos: elige el negocio a mano.`;
      }
    }
  });

  negocioSel.addEventListener('change', async () => {
    tarifa = 0;
    pintarDescuento();
    const id = negocioSel.value;
    if (!id) return;
    tarifaEl.textContent = 'Leyendo la tarifa...';
    const res = await SubscriptionApiClient.list(id);
    if (negocioSel.value !== id) return;
    tarifa = res.ok ? tarifaDeSuscripciones(res) : 0;
    if (!montoTocado && tarifa) montoInput.value = montoParaVer(tarifa);
    pintarDescuento();
    if (!res.ok) tarifaEl.textContent = 'No se pudo leer la tarifa: escribe el monto';
    else if (!tarifa) tarifaEl.textContent = 'Sin suscripcion con tarifa: escribe el monto';
  });

  montoInput.addEventListener('input', () => { montoTocado = true; pintarDescuento(); });
  montoInput.addEventListener('blur', () => {
    const n = leerMonto(montoInput.value);
    if (n) montoInput.value = montoParaVer(n);
  });
  mesInput.addEventListener('change', () => { mesTocado = true; });
  fechaInput.addEventListener('change', () => {
    mesInput.value = mesQueQueda(fechaInput.value, mesInput.value, mesTocado);
  });

  const leerFormulario = () => cuerpoDelPago({
    negocio: negocios.find((n) => n.id === negocioSel.value) || null,
    montoEscrito: montoInput.value,
    tarifa,
    medio: $('np-f-medio').value,
    fechaPago: fechaInput.value,
    mesCubierto: mesInput.value,
    canal: $('np-f-canal').value,
    notas: $('np-f-notas').value,
    motivoDescuento: $('np-f-motivo').value
  });

  const botones = [$('np-submit'), $('np-solo-registrar')];
  const ocupado = (si) => botones.forEach((b) => { b.disabled = si; });

  const despues = (cuerpo, mensaje) => {
    close();
    showToast(mensaje);
    mesFiltro = cuerpo.mesCubierto || mesFiltro;
    const mesSel = document.getElementById('np-mes');
    if (mesSel) mesSel.value = mesFiltro;
    loadPagos();
    loadCartera();
  };

  // Registrar y activar: una accion en la API (PAGOS-NEXO-ACTIVAR-API-T1).
  $('np-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    errEl.textContent = '';
    const r = leerFormulario();
    if (!r.ok) { errEl.textContent = r.error; return; }
    ocupado(true);
    const res = await NexoPagosApiClient.registrarYActivar(r.cuerpo);
    if (res.ok) {
      const hasta = activaHasta(res, r.cuerpo.mesCubierto);
      despues(r.cuerpo, `${r.cuerpo.negocioNombre}: pago de ${fmtCOP(r.cuerpo.monto)} registrado y activa hasta ${fmtFecha(hasta)}`);
      return;
    }
    errEl.textContent = motivoSinActivar(res).mensaje;
    ocupado(false);
  });

  // Registrar pago: el camino de antes (solo anota el pago, sin activar), ahora con business_id.
  $('np-solo-registrar').addEventListener('click', async () => {
    errEl.textContent = '';
    const r = leerFormulario();
    if (!r.ok) { errEl.textContent = r.error; return; }
    ocupado(true);
    const res = await NexoPagosApiClient.create(r.cuerpo);
    if (res.ok) {
      despues(r.cuerpo, `Pago de ${r.cuerpo.negocioNombre} por ${fmtCOP(r.cuerpo.monto)} registrado (sin activar)`);
      return;
    }
    errEl.textContent = res.error || 'Error registrando el pago';
    ocupado(false);
  });
}

export async function renderNexoPagos(container) {
  mesFiltro = mesActual();

  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Pagos Nexo <span style="font-size:0.875rem;color:#8b949e;font-weight:normal">v1.1</span></h1>
          <p style="font-size:0.875rem;color:#8b949e;margin-top:2px">
            Control de cobros de Nexo Digital &middot; suscripciones = ventas de Nexo &middot; solo equipo Nexo
          </p>
        </div>
      </div>

      <style>
        .np-card { background:#161b22;border:1px solid #30363d;border-radius:8px;padding:14px }
        .np-card-label { font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px }
        .np-card-value { font-size:1.5rem;font-weight:600;color:#c9d1d9 }
        .np-table { width:100%;border-collapse:collapse;font-size:0.875rem }
        .np-table th { text-align:left;padding:8px;border-bottom:1px solid #30363d;color:#8b949e;font-weight:500;font-size:0.875rem;text-transform:uppercase;white-space:nowrap }
        .np-table td { padding:8px;border-bottom:1px solid #21262d;vertical-align:top }
        .np-btn { background:#21262d;border:1px solid #30363d;color:#c9d1d9;padding:7px 14px;border-radius:6px;cursor:pointer;font-size:0.875rem }
        .np-btn:hover { background:#30363d }
        .np-label { display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e }
        .np-input { padding:8px 10px;background:#0d1117;border:1px solid #30363d;border-radius:6px;color:#c9d1d9;font-size:0.875rem;font-family:inherit }
        .np-deudor { background:#161b22;border:1px solid #30363d;border-radius:8px;padding:12px }
        .np-mes-deuda { font-size:0.875rem;padding:6px 10px;border-color:#d2992255 }
      </style>

      <div id="np-cartera" data-cartera style="margin-bottom:26px"></div>

      <h2 style="font-size:0.95rem;margin:0 0 10px;color:#c9d1d9">Pagos registrados</h2>

      <div style="display:flex;gap:10px;align-items:center;margin-bottom:16px;flex-wrap:wrap">
        <label style="display:flex;align-items:center;gap:8px;font-size:0.875rem;color:#8b949e">Mes
          <input id="np-mes" type="month" value="${mesFiltro}" class="np-input">
        </label>
        <button id="np-todos" class="np-btn">Ver todos</button>
        <button id="np-nuevo" class="np-btn" style="margin-left:auto;background:#238636;border-color:#2ea043;color:#fff">+ Registrar pago</button>
      </div>

      <div id="np-content"></div>
    </section>
  `;

  document.getElementById('np-mes')?.addEventListener('change', (e) => {
    mesFiltro = e.target.value || null;
    loadPagos();
  });
  document.getElementById('np-todos')?.addEventListener('click', () => {
    mesFiltro = null;
    const mesSel = document.getElementById('np-mes');
    if (mesSel) mesSel.value = '';
    loadPagos();
  });
  document.getElementById('np-nuevo')?.addEventListener('click', () => openModalAlta());

  loadCartera();
  loadPagos();
}
